import { Link } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import CompareAddressFinder from '@/components/compare/CompareAddressFinder';
import type { DirectoryTownSummary } from '@/sanity/directory';
import type { BranchFees } from '@/utils/fee-maths';

import type {
  DirectoryAgentCost_agent$data,
  DirectoryAgentCost_agent$key,
} from './__generated__/DirectoryAgentCost_agent.graphql';
import DirectoryFeeDotPlot from './DirectoryFeeDotPlot';
import useDirectoryFeeControls from '../hooks/use-directory-fee-controls';
import {
  formatDirectoryAgentCostHeadline,
  formatFeePercentNote,
  formatVatFootnote,
  getDirectoryAgentCost,
  type DirectoryAgentCostService,
} from '../utils/directory-agent-cost';
import {
  directoryFeeServiceLabels,
  formatFeePercent,
  formatMonthlyRentPounds,
  formatPounds,
  formatServiceLabelLowercase,
  formatSignedPounds,
} from '../utils/directory-fee-display';
import { findTownForAreas } from '../utils/town-for-areas';

const branchServices: DirectoryAgentCostService[] = [
  'management',
  'rentCollection',
];

type DirectoryAgentCostProps = {
  agent: DirectoryAgentCost_agent$key;
  towns: readonly DirectoryTownSummary[];
};

function readBranchFeesForCost(
  fees: DirectoryAgentCost_agent$data['fees'],
): BranchFees | null {
  if (fees == null) {
    return null;
  }

  return {
    feesIncludeVat: fees.feesIncludeVat,
    management: fees.management,
    rentCollection: fees.rentCollection,
    tenantFind: fees.tenantFind,
    letOnly: fees.letOnly,
    renewal: fees.renewal,
    inventory: fees.inventory,
    managementWithTenantInPlace: fees.managementWithTenantInPlace,
    rentCollectionWithTenantInPlace: fees.rentCollectionWithTenantInPlace,
    otherFees:
      fees.otherFees == null
        ? null
        : fees.otherFees.map((fee) => ({
            label: fee.label,
            percent: fee.percent,
            amountPence: fee.amountPence,
          })),
  };
}

function readAreaStatsForCost(
  primaryArea: DirectoryAgentCost_agent$data['primaryArea'],
) {
  const stats = primaryArea?.stats;
  if (stats == null) {
    return null;
  }

  const mapServiceStats = (serviceStats: (typeof stats)['management']) =>
    serviceStats == null
      ? null
      : {
          sampleSize: serviceStats.sampleSize,
          minimumPercentIncVat: serviceStats.minimumPercentIncVat,
          medianPercentIncVat: serviceStats.medianPercentIncVat,
          maximumPercentIncVat: serviceStats.maximumPercentIncVat,
          percentsIncVat: serviceStats.percentsIncVat,
        };

  return {
    management: mapServiceStats(stats.management),
    rentCollection: mapServiceStats(stats.rentCollection),
  };
}

const DirectoryAgentCost = ({
  agent: agentKey,
  towns,
}: DirectoryAgentCostProps) => {
  const agent = useFragment(
    graphql`
      fragment DirectoryAgentCost_agent on LandlordDirectoryAgent {
        name
        fees {
          feesIncludeVat
          management {
            percent
            amountPence
          }
          rentCollection {
            percent
            amountPence
          }
          tenantFind {
            percent
            amountPence
          }
          letOnly {
            percent
            amountPence
          }
          renewal {
            percent
            amountPence
          }
          inventory {
            percent
            amountPence
          }
          managementWithTenantInPlace {
            percent
            amountPence
          }
          rentCollectionWithTenantInPlace {
            percent
            amountPence
          }
          otherFees {
            label
            percent
            amountPence
          }
        }
        primaryArea @featureFlag(gate: "directory_branch_page_v2") {
          name
          slug
          stats {
            management {
              sampleSize
              minimumPercentIncVat
              medianPercentIncVat
              maximumPercentIncVat
              percentsIncVat
            }
            rentCollection {
              sampleSize
              minimumPercentIncVat
              medianPercentIncVat
              maximumPercentIncVat
              percentsIncVat
            }
          }
        }
      }
    `,
    agentKey,
  );

  const feeControls = useDirectoryFeeControls();
  const service =
    feeControls.service === 'letOnly' ? 'management' : feeControls.service;

  const agentName = agent.name ?? 'This agent';
  const areaName = agent.primaryArea?.name ?? null;
  const areaStats = readAreaStatsForCost(agent.primaryArea);
  const town = findTownForAreas(
    towns,
    agent.primaryArea?.slug == null ? [] : [agent.primaryArea.slug],
  );

  const view = getDirectoryAgentCost({
    agentName,
    fees: readBranchFeesForCost(agent.fees),
    service,
    monthlyRentPence: feeControls.monthlyRentPence,
    areaName,
    areaStats,
  });

  const headline = formatDirectoryAgentCostHeadline({
    agentName,
    service,
    view,
  });

  const rentNote = feeControls.isExampleRent
    ? `Shown for a £${formatMonthlyRentPounds(feeControls.monthlyRentPence)}/month let. Enter your rent to see your own figures.`
    : '';

  const vatFootnote = formatVatFootnote(agent.fees?.feesIncludeVat);

  const handleRentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    feeControls.setRentInput(event.target.value);
  };

  const negotiablePanel = (
    <div className="rounded-2xl bg-accent-tint p-4">
      <p className="m-0 text-[15px] font-semibold">Fees are often negotiable</p>
      <p className="mt-0.5 mb-3 text-[13px] text-ink-soft">
        We&apos;ll ask {agentName} and other local agents to price your
        property, and negotiate for you.
      </p>
      <CompareAddressFinder
        addressPlaceholder="Start typing your address"
        className="gap-2 [&_button]:h-12 [&_input]:h-12"
        placement="directory_agent_cost"
        submitLabel="Get a better deal"
      />
    </div>
  );

  return (
    <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:grid-rows-[auto_auto] lg:gap-8">
      <div className="order-1 flex flex-col gap-3 lg:col-start-1 lg:row-start-1">
        <p className="m-0 text-sm font-medium text-accent-strong">
          What it costs
        </p>
        <h2 className="m-0 text-[28px] leading-[1.15] font-medium tracking-[-0.03em] text-balance">
          {headline}
        </h2>
        {view.kind === 'noFees' ? (
          <p className="m-0 text-[15px] text-ink-soft">
            Letting agents must publish their fees, including VAT. We
            couldn&apos;t find them for {agentName}.
          </p>
        ) : null}
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm font-medium">
          <Link
            className="text-accent-strong no-underline hover:text-ink"
            params={{ slug: 'letting-agent-fees' }}
            to="/$slug"
          >
            Letting agent fees explained
          </Link>
          {town != null ? (
            <Link
              className="text-accent-strong no-underline hover:text-ink"
              params={{ townSlug: town.slug }}
              to="/letting-agents/$townSlug"
            >
              Compare {town.name} agents&apos; fees
            </Link>
          ) : null}
        </div>
        {view.kind === 'noFees' ? (
          <p className="m-0 text-[13px] text-ink-muted">
            None of the 8 standard fees is published.
          </p>
        ) : null}
      </div>

      <div className="surface-card order-2 flex flex-col gap-5 p-5 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1">
        {view.kind === 'noFees' ? (
          <h3 className="m-0 text-lg font-medium">
            What {areaName ?? 'local'} agents charge
          </h3>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div
            aria-label="Service level"
            className="inline-flex gap-1 rounded-full bg-chip p-1"
            role="group"
          >
            {branchServices.map((option) => {
              const isSelected = service === option;
              const labels = directoryFeeServiceLabels[option];

              return (
                <button
                  aria-pressed={isSelected}
                  className="h-10 rounded-full px-4 text-sm font-medium text-ink transition-[background-color,box-shadow] duration-150 ease-out aria-pressed:bg-white aria-pressed:shadow-[0_1px_2px_rgb(14_14_12/0.12)] motion-reduce:transition-none"
                  key={option}
                  onClick={() => feeControls.setService(option)}
                  type="button"
                >
                  <span className="sm:hidden">{labels.mobile}</span>
                  <span className="hidden sm:inline">{labels.desktop}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2.5">
            <label
              className="text-sm text-ink-soft"
              htmlFor="directory-branch-rent"
            >
              Monthly rent
            </label>
            <div className="relative">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-[15px] text-ink-muted"
              >
                £
              </span>
              <input
                className="box-border h-11 w-32 rounded-full border border-line-field bg-white pr-3.5 pl-7 text-[15px] font-medium text-ink tabular-nums outline-none"
                id="directory-branch-rent"
                inputMode="numeric"
                onChange={handleRentChange}
                value={feeControls.rentInput}
              />
            </div>
          </div>
        </div>
        <p className="m-0 text-[13px] text-ink-muted">
          {[rentNote, vatFootnote].filter(Boolean).join(' ')}
        </p>
        <DirectoryAgentCostMetrics
          agentName={agentName}
          areaName={areaName}
          service={service}
          town={town}
          view={view}
        />
      </div>

      <div className="order-3 lg:col-start-1 lg:row-start-2">
        {negotiablePanel}
      </div>
    </section>
  );
};

const DirectoryAgentCostMetrics = ({
  agentName,
  areaName,
  service,
  town,
  view,
}: {
  agentName: string;
  areaName: string | null;
  service: DirectoryAgentCostService;
  town: DirectoryTownSummary | null;
  view: ReturnType<typeof getDirectoryAgentCost>;
}) => {
  if (view.kind === 'noFees') {
    return (
      <>
        <div className="flex flex-wrap gap-6 sm:gap-10">
          <CostTile
            label="Typical fee"
            note={`${formatServiceLabelLowercase(service)}, inc VAT`}
            value={formatFeePercent(view.medianPercentIncVat)}
          />
          <CostTile
            label="Per year at this rent"
            note={`${areaName ?? 'Area'} median`}
            value={formatPounds(view.medianCostPence)}
          />
          <CostTile
            isMuted
            label={agentName.split(' ')[0] ?? agentName}
            note={
              town != null ? (
                <Link
                  className="text-accent-strong no-underline hover:text-ink"
                  params={{ townSlug: town.slug }}
                  to="/letting-agents/$townSlug"
                >
                  Compare agents that publish fees
                </Link>
              ) : (
                'Not published'
              )
            }
            value="Not published"
          />
        </div>
        <DirectoryFeeDotPlot
          areaName={areaName ?? 'Area'}
          ariaLabel={`${areaName ?? 'Area'} fee distribution; ${agentName} not published`}
          highlight={null}
          maximumPercentIncVat={view.maximumPercentIncVat}
          medianPercentIncVat={view.medianPercentIncVat}
          medianPosition={view.medianPosition}
          minimumPercentIncVat={view.minimumPercentIncVat}
          notPublishedLabel={`${agentName.split(' ')[0] ?? agentName}: not published`}
          percentsIncVat={view.percentsIncVat}
        />
      </>
    );
  }

  if (view.kind === 'serviceNotPublished' || view.kind === 'notComparable') {
    const publishedValue =
      view.kind === 'notComparable' && view.publishedPercent != null
        ? formatFeePercent(view.publishedPercent)
        : view.kind === 'notComparable' && view.publishedAmountPence != null
          ? formatPounds(view.publishedAmountPence)
          : 'Not published';

    const reasonNote =
      view.kind === 'notComparable' && view.reason === 'vatNotStated'
        ? 'VAT not stated'
        : view.kind === 'notComparable' && view.reason === 'periodNotStated'
          ? 'Period not stated'
          : 'Not published';

    return (
      <div className="flex flex-wrap gap-6 sm:gap-10">
        <CostTile label="Fee" note={reasonNote} value={publishedValue} />
      </div>
    );
  }

  const percentNote = formatFeePercentNote({
    percentIncVat: view.percentIncVat,
    publishedPercentExVat: view.publishedPercentExVat,
    isVatAdded: view.isVatAdded,
  });

  const comparison = view.comparison;
  const diffColor =
    comparison != null && comparison.differencePence > 0
      ? 'text-caution'
      : comparison != null && comparison.differencePence <= 0
        ? 'text-accent-strong'
        : 'text-ink';

  const diffNote =
    comparison == null
      ? null
      : comparison.differencePence === 0
        ? `the same as the median (${formatFeePercent(comparison.medianPercentIncVat)})`
        : comparison.differencePoints > 0
          ? `a year more than the median (${formatFeePercent(comparison.medianPercentIncVat)})`
          : `a year less than the median (${formatFeePercent(comparison.medianPercentIncVat)})`;

  return (
    <>
      <div className="grid gap-6 sm:grid-cols-3 sm:gap-10">
        <CostTile
          label="Fee"
          note={percentNote ?? 'inc VAT'}
          value={formatFeePercent(view.percentIncVat)}
        />
        <CostTile
          label="Per year"
          note="+ set-up fees, not published"
          value={formatPounds(view.costPence)}
        />
        {comparison != null && areaName != null ? (
          <CostTile
            className="sm:col-span-3 sm:col-auto"
            label={`Vs typical ${areaName} agent`}
            note={diffNote ?? undefined}
            value={formatSignedPounds(comparison.differencePence)}
            valueClassName={diffColor}
          />
        ) : null}
      </div>
      {comparison != null && areaName != null ? (
        <DirectoryFeeDotPlot
          areaName={areaName}
          ariaLabel={comparison.dotPlotAriaLabel}
          highlight={{
            label: agentName.split(' ')[0] ?? agentName,
            percentIncVat: view.percentIncVat,
            position: comparison.agentPosition,
          }}
          maximumPercentIncVat={comparison.maximumPercentIncVat}
          medianPercentIncVat={comparison.medianPercentIncVat}
          medianPosition={comparison.medianPosition}
          minimumPercentIncVat={comparison.minimumPercentIncVat}
          percentsIncVat={comparison.percentsIncVat}
        />
      ) : null}
    </>
  );
};

const CostTile = ({
  className,
  isMuted = false,
  label,
  note,
  value,
  valueClassName,
}: {
  className?: string;
  isMuted?: boolean;
  label: string;
  note?: ReactNode;
  value: ReactNode;
  valueClassName?: string;
}) => (
  <div className={className}>
    <p className="m-0 text-[13px] text-ink-muted">{label}</p>
    <p
      className={
        isMuted
          ? `m-0 text-[clamp(28px,6vw,40px)] leading-[1.1] font-medium tracking-[-0.04em] text-ink-muted tabular-nums ${valueClassName ?? ''}`
          : `m-0 text-[clamp(28px,6vw,40px)] leading-[1.1] font-medium tracking-[-0.04em] tabular-nums ${valueClassName ?? ''}`
      }
    >
      {value}
    </p>
    {note != null ? (
      <div className="mt-0 text-[13px] text-ink-muted">{note}</div>
    ) : null}
  </div>
);

export default DirectoryAgentCost;
